<script setup lang="ts">
import NextOffRaces from "~/components/layout/TrackRacing/FixedOdds/NextOffRaces/NextOffRaces.vue";
import DailyRaces from "~/components/layout/TrackRacing/FixedOdds/DailyRaces/DailyRaces.vue";
import AntePost from "~/components/layout/TrackRacing/FixedOdds/AntePost/AntePost.vue";
import { isMobile } from "~/utils/constants";
import t from "~/utils/translate";
import { useSettings } from "~/store/settings";
const router = useRouter();
const route = useRoute();
interface Props {
	sportId?: string | undefined;
}

interface IFixeOddsTabs {
	DisplayName: string;
	Key: string;
	TranslationKey: string;
	Config?: {
		active: boolean;
		league: string;
		expectStartEpoch: number;
		expectedEndEpoch: number;
		sportId: string;
	};
}

const settings = useSettings();
const props = defineProps<Props>();
const selectedNav = ref<string | null>(null);
const specialEventType = ref<string | undefined>(undefined);
const isSpecialTab = ref<boolean>(false);

const fixedOddsTabs = computed(() => {
	const key = props.sportId === "horse-racing" ? "HorseRacing" : "GreyHounds";
	const items = settings.config?.devConfig?.RacingNavigation ? settings.config?.devConfig?.RacingNavigation[key] : [];
	return items as IFixeOddsTabs[];
});

const racingNavItems = computed(() => {
	const currentEpochTime = Math.floor(Date.now() / 1000);
	return fixedOddsTabs.value.filter(
		(tab) => (tab.Config?.active && tab.Config.expectedEndEpoch > currentEpochTime && tab.Config.sportId === selectedSportId.value) || !tab.Config
	);
});
const selectedSportId = computed(() => {
	return props.sportId;
});

const handleSpecialTabs = (config: { active: boolean; expectStartEpoch: number; expectedEndEpoch: number }) => {
	const expectedStartDate = new Date(config.expectStartEpoch * 1000);
	const oneDay = 24 * 60 * 60 * 1000;
	const diffDays = Math.round(Math.abs((+expectedStartDate - +new Date()) / oneDay));
	specialEventType.value = diffDays === 0 ? "today" : diffDays === 1 ? "tomorrow" : "future";
	if (["today", "tomorrow"].includes(specialEventType.value)) {
		isSpecialTab.value = true;
	}
};

const handleSelectedNav = (navValue: string, config?: { active: boolean; expectStartEpoch: number; expectedEndEpoch: number; league: string }) => {
	selectedNav.value = navValue === "custom" && config ? config.league : navValue;
	if (config) {
		handleSpecialTabs(config);
	} else {
		isSpecialTab.value = false;
	}
	router.push({ query: { eventType: selectedNav.value } });
};

const handleEventSelection = (selection: { dailySelection: string; eventId: number; regionId: string; league: string }) => {
	selectedNav.value = selection.dailySelection;
	router.push({
		query: {
			...route.query,
			regionId: selection.regionId,
			league: selection.league,
			eventType: selection.dailySelection,
			eventId: selection.eventId,
			isNextOff: "true",
		},
	});
};

const eventType = computed(() => route.query?.eventType);

const hammer = ref();
onMounted(() => {
	selectedNav.value = eventType && typeof eventType.value === "string" ? eventType.value : "nextOff";
	const specialTab = racingNavItems.value.find((tab) => tab.Key === "custom" && tab.Config);
	if (specialTab?.Config) {
		handleSpecialTabs(specialTab.Config);
	}

	if (isMobile) {
		try {
			const swipeableEl = document.getElementById("racing-container") as HTMLElement;
			// @ts-ignore
			hammer.value = new Hammer(swipeableEl);

			const handleMarketChange = (direction: "swipeleft" | "swiperight") => {
				if (racingNavItems.value && racingNavItems.value.length > 1) {
					const activeTab = selectedNav.value;
					const tabs = [...racingNavItems.value];
					const currentIndex = racingNavItems.value.findIndex((tab) => activeTab === tab.Key);
					const nextIndex =
						direction === "swipeleft"
							? currentIndex === tabs.length - 1
								? 0
								: currentIndex + 1
							: currentIndex === 0
							? tabs.length - 1
							: currentIndex - 1;
					const nextTab = tabs[nextIndex];
					handleSelectedNav(nextTab.Key, nextTab.Config);
				}
			};

			hammer.value.on("swipeleft", () => handleMarketChange("swipeleft"));
			hammer.value.on("swiperight", () => handleMarketChange("swiperight"));
		} catch (e) {
			console.log("Swipe Error", e);
		}
	}
});

onUnmounted(() => (hammer.value = undefined));

watch(selectedSportId, (newValue, oldValue) => {
	if (newValue !== oldValue) {
		selectedNav.value = "nextOff";
	}
});

watch(eventType, (newValue, oldValue) => {
	if (newValue !== oldValue) {
		selectedNav.value = newValue?.toString() || "nextOff";
	}
});
</script>

<template>
	<div>
		<div class="flex gap-1 overflow-x-auto overflow-y-hidden scrollbar-hidden bg-dark-800 h-[40px] rounded-t-lg">
			<div
				v-for="navItem in racingNavItems"
				:key="navItem.Key"
				:class="[
					'text-sm font-semibold text-center transition-all border-b-4 cursor-pointer whitespace-nowrap border-b-[transparent] text-light-50',
					{
						'text-identity border-b-identity text-primary-600':
							navItem.Key === selectedNav || (navItem.Config && navItem.Config.league === selectedNav),
					},
				]"
				@click="handleSelectedNav(navItem.Key, navItem.Config)"
			>
				<span class="min-w-[60px] block my-2 px-4">
					{{ t(navItem.TranslationKey) }}
				</span>
			</div>
		</div>
		<div id="racing-container">
			<div v-if="selectedNav === 'nextOff'" class="p-1 rounded-b-lg border border-solid border-light-400 dark:border-dark-400">
				<NextOffRaces @event-selected="handleEventSelection($event)" />
			</div>
			<DailyRaces
				v-if="['today', 'tomorrow'].includes(selectedNav || '') || (isSpecialTab && specialEventType !== 'future' && selectedNav !== 'nextOff')"
				:sport-id="sportId?.toString()"
				:selected-period="isSpecialTab && isSpecialTab ? specialEventType : selectedNav || undefined"
				:is-special-selection="isSpecialTab && !['today', 'tomorrow'].includes(eventType?.toString() || '')"
			/>
			<AntePost
				v-if="
					selectedNav === 'future' ||
					selectedNav === 'antepost' ||
					(specialEventType === 'future' && !['nextOff', 'today', 'tomorrow'].includes(selectedNav || ''))
				"
				:sport-id="sportId"
				:selectedNav="isSpecialTab && isSpecialTab ? specialEventType : selectedNav || undefined"
				:isSpecialTab="specialEventType === 'future' && !['antepost', 'future'].includes(eventType as string || '')"
			/>
		</div>
	</div>
</template>
